import { X } from "lucide-react";
import React, { useState } from "react";
import { formatSignedPercentage } from "../../../utils/numberUtils";

export type StrategyPositionCounts = { long: number; short: number; options: number };

export function PositionCountCell({
  counts,
  appearance = "default",
}: {
  counts: StrategyPositionCounts | null;
  appearance?: "default" | "alpha";
}) {
  const isAlpha = appearance === "alpha";
  const [open, setOpen] = useState(false);
  if (!counts) return <span className={isAlpha ? "text-v7-ink/40" : "text-gray-400 dark:text-gray-500"}>--</span>;
  const total = counts.long + counts.short + counts.options;
  return (
    <>
      <button
        type="button"
        onClick={(e) => { e.stopPropagation(); setOpen(true); }}
        className={`cursor-pointer font-mono text-sm transition-colors hover:underline ${isAlpha ? "text-v7-ink hover:text-v7-bronze-dark" : "text-gray-900 hover:text-rp-amber dark:text-white"}`}
      >
        {total}
      </button>
      {open && (
        <div
          className={`fixed inset-0 z-50 flex items-center justify-center p-4 ${isAlpha ? "bg-v7-ink/70 backdrop-blur-sm" : "bg-black/70"}`}
          role="dialog"
          aria-modal="true"
          aria-label="Position Breakdown"
          onClick={() => setOpen(false)}
        >
          <div
            data-testid="position-breakdown-modal-panel"
            data-appearance={appearance}
            className={`w-64 overflow-hidden border shadow-2xl ${isAlpha ? "rounded-v7-popover border-v7-ink/15 bg-v7-white font-v7-sans" : "rounded-xl border-gray-200 bg-white dark:border-rp-separator dark:bg-rp-surface-raised"}`}
            onClick={(e) => e.stopPropagation()}
          >
            <div className={`flex items-center justify-between border-b px-4 py-3 ${isAlpha ? "border-v7-ink/15" : "border-gray-200 dark:border-rp-separator"}`}>
              <span className={`text-sm font-semibold ${isAlpha ? "text-v7-ink" : "text-gray-900 dark:text-white"}`}>Position Breakdown</span>
              <button
                onClick={() => setOpen(false)}
                aria-label="Close position breakdown"
                className={isAlpha ? "rounded-v7-control p-1 text-v7-ink/60 transition-colors hover:bg-v7-paper hover:text-v7-ink" : "rounded p-1 text-gray-400 transition-colors hover:bg-gray-800 hover:text-gray-200"}
              >
                <X className="w-4 h-4" />
              </button>
            </div>
            <div className="px-4 py-4 space-y-3">
              <div className="flex items-center justify-between">
                <span className={`text-xs uppercase tracking-wider ${isAlpha ? "text-v7-ink/60" : "text-gray-500 dark:text-gray-400"}`}>Long</span>
                <span className={`font-mono text-sm ${isAlpha ? "text-v7-ink" : "text-gray-900 dark:text-white"}`}>{counts.long}</span>
              </div>
              <div className="flex items-center justify-between">
                <span className={`text-xs uppercase tracking-wider ${isAlpha ? "text-v7-ink/60" : "text-gray-500 dark:text-gray-400"}`}>Short</span>
                <span className={`font-mono text-sm ${isAlpha ? "text-v7-ink" : "text-gray-900 dark:text-white"}`}>{counts.short}</span>
              </div>
              <div className="flex items-center justify-between">
                <span className={`text-xs uppercase tracking-wider ${isAlpha ? "text-v7-ink/60" : "text-gray-500 dark:text-gray-400"}`}>Options</span>
                <span className={`font-mono text-sm ${isAlpha ? "text-v7-ink" : "text-gray-900 dark:text-white"}`}>{counts.options}</span>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

export function SinceAddedCell({
  value,
  compact = false,
  appearance = "default",
}: {
  value: { addedAtUtc: string | null; returnPct: number | null } | null;
  compact?: boolean;
  appearance?: "default" | "alpha";
}) {
  const isAlpha = appearance === "alpha";
  const [open, setOpen] = useState(false);
  const addedDate = formatSinceAddedDateUtc(value?.addedAtUtc ?? null);

  if (!addedDate) {
    return <ReturnCell value={value?.returnPct ?? null} appearance={appearance} />;
  }

  return (
    <>
      <button
        type="button"
        onClick={(e) => {
          e.stopPropagation();
          setOpen(true);
        }}
        className={`${compact ? "text-xs" : "text-sm"} cursor-pointer font-mono transition-colors hover:underline ${isAlpha ? "hover:text-v7-bronze-dark" : "hover:text-rp-amber"}`}
      >
        <ReturnCell value={value?.returnPct ?? null} appearance={appearance} />
      </button>
      {open && (
        <div
          className={`fixed inset-0 z-50 flex items-center justify-center p-4 ${isAlpha ? "bg-v7-ink/70 backdrop-blur-sm" : "bg-black/70"}`}
          role="dialog"
          aria-modal="true"
          aria-label="Since Added"
          onClick={() => setOpen(false)}
        >
          <div
            data-testid="since-added-modal-panel"
            data-appearance={appearance}
            className={`w-72 overflow-hidden border shadow-2xl ${isAlpha ? "rounded-v7-popover border-v7-ink/15 bg-v7-white font-v7-sans" : "rounded-xl border-gray-200 bg-white dark:border-rp-separator dark:bg-rp-surface-raised"}`}
            onClick={(e) => e.stopPropagation()}
          >
            <div className={`flex items-center justify-between border-b px-4 py-3 ${isAlpha ? "border-v7-ink/15" : "border-gray-200 dark:border-rp-separator"}`}>
              <span className={`text-sm font-semibold ${isAlpha ? "text-v7-ink" : "text-gray-900 dark:text-white"}`}>Since Added</span>
              <button
                onClick={() => setOpen(false)}
                aria-label="Close since added details"
                className={isAlpha ? "rounded-v7-control p-1 text-v7-ink/60 transition-colors hover:bg-v7-paper hover:text-v7-ink" : "rounded p-1 text-gray-400 transition-colors hover:bg-gray-800 hover:text-gray-200"}
              >
                <X className="w-4 h-4" />
              </button>
            </div>
            <div className="px-4 py-4 space-y-3">
              <div className="flex items-center justify-between gap-4">
                <span className={`text-xs uppercase tracking-wider ${isAlpha ? "text-v7-ink/60" : "text-gray-500 dark:text-gray-400"}`}>Added</span>
                <span className={`text-right font-mono text-sm ${isAlpha ? "text-v7-ink" : "text-gray-900 dark:text-white"}`}>{addedDate}</span>
              </div>
              <div className="flex items-center justify-between gap-4">
                <span className={`text-xs uppercase tracking-wider ${isAlpha ? "text-v7-ink/60" : "text-gray-500 dark:text-gray-400"}`}>Return</span>
                <ReturnCell value={value?.returnPct ?? null} appearance={appearance} />
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

export function ReturnCell({
  value,
  appearance = "default",
}: {
  value: number | null;
  appearance?: "default" | "alpha";
}) {
  const isAlpha = appearance === "alpha";
  if (value == null) {
    return <span className={isAlpha ? "text-v7-ink/40" : "text-gray-400 dark:text-gray-500"}>--</span>;
  }
  const color =
    value > 0
      ? isAlpha ? "text-v7-success" : "text-green-600 dark:text-green-500"
      : value < 0
        ? isAlpha ? "text-v7-rust" : "text-red-600 dark:text-red-500"
        : isAlpha ? "text-v7-ink/40" : "text-gray-400 dark:text-gray-500";
  return <span className={`font-mono text-sm ${color}`}>{formatSignedPercentage(value)}</span>;
}

export function DriftCell({
  drift,
  appearance = "default",
}: {
  drift: number | null;
  appearance?: "default" | "alpha";
}) {
  const isAlpha = appearance === "alpha";
  if (drift == null) {
    return <span className={isAlpha ? "text-v7-ink/40" : "text-gray-400 dark:text-gray-500"}>--</span>;
  }

  const color =
    drift > 0
      ? isAlpha ? "text-v7-success" : "text-green-500"
      : drift < 0
        ? isAlpha ? "text-v7-rust" : "text-red-500"
        : isAlpha ? "text-v7-ink/40" : "text-gray-400 dark:text-gray-500";

  return <span className={color}>{formatSignedPercentage(drift)}</span>;
}

const MONTH_LABELS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

export function formatSinceAddedDateUtc(addedAtUtc: string | null): string | null {
  if (!addedAtUtc) return null;
  const date = new Date(addedAtUtc);
  if (Number.isNaN(date.getTime())) return null;
  const month = MONTH_LABELS[date.getUTCMonth()];
  const day = date.getUTCDate();
  const year = date.getUTCFullYear();
  const hours = String(date.getUTCHours()).padStart(2, "0");
  const minutes = String(date.getUTCMinutes()).padStart(2, "0");
  return `${month} ${day}, ${year}, ${hours}:${minutes} UTC`;
}
